<template>
  <form
    id="indicator-form"
    @submit.prevent="$emit('submit')"
  >
    <fieldset class="px-8 pt-6 pb-2 max-w-full [&_.input-field]:!max-w-full [&_.input-field]:!w-full border-b border-modal-divider">
      <TextInput
        v-model="model.name"
        label="Name of Strategy / Priority / KPA"
        :validation-error="error"
        required
      />

      <TextEditorInput
        id="modal-indicator-description"
        v-model="model.description"
        :inline="false"
        class="!my-6"
        label="Description of Strategy / Priority / KPA"
      />
    </fieldset>

    <!-- Strategic Framework Alignments Section -->
    <div class="p-8">
      <h3 class="text-black text-md mb-1">
        Strategic Framework Alignments
      </h3>

      <span class="text-gray-600 block mb-4">
        Select the frameworks and outcomes this project contributes towards.
      </span>

      <div class="mb-6">
        <label class="text-black text-sm font-medium mb-1 block">
          Add a Framework
        </label>

        <!-- Emits the ID of the selected framework -->
        <FrameworkSearchForm
          v-model:search-params="searchParams"
          v-model:show-search-results="showSearchResults"
          :search-results="availableFrameworkOptions"
          @select="handleFrameworkSelect"
        />
      </div>

      <span
        v-if="model.frameworkAlignments?.length"
        class="block mb-2 mt-6 !text-base"
      >
        SELECTED ALIGNMENTS ({{ model.frameworkAlignments.length }} Frameworks)
      </span>

      <!-- Collapsible accordion cards of selected frameworks -->
      <div
        v-for="(alignment, index) in model.frameworkAlignments"
        :key="alignment.frameworkId"
        class="border border-gray-300 bg-white overflow-hidden mb-4 last:mb-0"
      >
        <div
          class="flex justify-between items-center p-4 cursor-pointer hover:bg-gray-50 transition-colors select-none"
          @click="expandedFrameworks[alignment.frameworkId] = !expandedFrameworks[alignment.frameworkId]"
        >
          <div class="flex items-center gap-3">
            <div class="text-gray-600 w-4 text-center">
              <FontAwesomeIcon
                :icon="faChevronUp"
                class="transition-transform duration-200"
                :class="{ '-rotate-180': !expandedFrameworks[alignment.frameworkId] }"
              />
            </div>

            <!-- Framework Details -->
            <div class="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-2">
              <h4 class="font-bold text-gray-900 m-0 text-sm">
                {{ getFrameworkName(alignment.frameworkId) }}
              </h4>

              <span class="text-gray-600 text-sm font-normal">
                ({{ getSelectedCount(alignment) }} outcomes selected)
              </span>
            </div>
          </div>

          <!-- Actions: View Overview Modal & Remove Framework -->
          <div class="flex items-center gap-3">
            <button
              type="button"
              class="flex items-center gap-1.5 px-1 text-primary hover:text-primary-dark hover:outline-primary hover:outline-opacity-50 hover:outline-[1px] text-sm"
              @click.stop="frameworkForOverview = frameworks[alignment.frameworkId]"
            >
              <FontAwesomeIcon
                :icon="faInfoCircle"
                class="mb-[1px]"
              />
              Framework overview
            </button>

            <AppTooltip>
              <template #reference>
                <button
                  type="button"
                  class="text-chrome-dark hover:text-danger transition-colors p-1"
                  @click.stop="removeFramework(index)"
                >
                  <FontAwesomeIcon :icon="faTrashCan" />
                </button>
              </template>

              Remove Framework
            </AppTooltip>
          </div>
        </div>

        <!-- Accordion Body: Contains the Tree of Goals and Subgoals with Checkboxes. -->
        <div
          v-show="expandedFrameworks[alignment.frameworkId]"
          class="relative px-5 py-1 bg-white"
        >
          <p class="text-gray-600 text-sm mb-4">
            Select outcomes below
          </p>

          <div v-if="loadingFrameworks[alignment.frameworkId]">
            <span class="text-sm text-gray-500 italic">Loading outcomes...</span>
          </div>

          <!-- Goal Tree -->
          <template v-else>
            <div
              v-for="(goal, gIdx) in frameworks[alignment.frameworkId]?.goals"
              :key="goal.id"
              class="goal-item"
            >
              <h5
                v-if="goal.subgoals?.length"
                class="text-sm font-semibold text-chrome-dark mt-4 mb-2"
              >
                {{ goal.name }}
              </h5>

              <label
                v-for="(item) in (goal.subgoals?.length ? goal.subgoals : [goal])"
                :key="item.id"
                class="input-field mb-3"
              >
                <input
                  type="checkbox"
                  class="!w-4.5 !h-4.5"
                  :checked="goal.subgoals?.length
                    ? isSubgoalSelected(alignment, goal.id, item.id)
                    : isGoalSelected(alignment, goal.id)"
                  @change="goal.subgoals?.length
                    ? toggleSubgoal(alignment, goal.id, item.id)
                    : toggleGoal(alignment, goal.id)"
                >

                <span class="!text-chrome-dark !text-base">
                  {{ item.name }}
                </span>
              </label>
            </div>

            <!-- Empty State -->
            <div
              v-if="!frameworks[alignment.frameworkId]?.goals.length"
              class="text-gray-500 text-sm italic"
            >
              No outcomes found for this framework.
            </div>
          </template>
        </div>
      </div>
    </div>

    <!-- Details Modal -->
    <FrameworkOverviewModal
      v-if="frameworkForOverview"
      :framework-id="frameworkForOverview.id"
      @close="frameworkForOverview = null"
    />
  </form>
</template>

<script setup>
import { HttpStatusCode } from 'axios';
import { computed, onMounted, reactive, ref } from 'vue';

import { TextInput } from '@/components/form-inputs';
import { FrameworkSearchForm } from '@/components/forms/search';
import { FrameworkOverviewModal } from '@/components/framework';
import { TextEditorInput } from '@/components/form-inputs';

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faTrashCan, faChevronUp, faInfoCircle } from '@fortawesome/pro-solid-svg-icons';

import { useHttp } from '@/composables';
import { AppTooltip } from '@/components/app';

defineEmits(['submit']);

defineProps({
  error: {
    type: String,
    default: null,
  },
});

const model = defineModel({
  default: () => ({
    id: null,
    name: null,
    description: null,
    frameworkAlignments: [],
  }),
});

const showSearchResults = ref(false);
const searchParams = reactive({
  page: 1,
  search: null,
});
const allFrameworks = ref([]);
const frameworks = ref({});

const loadingFrameworks = ref({});
const expandedFrameworks = ref({});
const frameworkForOverview = ref(null);

// If we are editing an existing strategy, we loop through the saved alignments
// and fetch the framework details/goals for each so the UI can render the names and checkboxes.
onMounted(async () => {
  await loadFrameworks();

  if (model.value.frameworkAlignments?.length) {
    await Promise.all(
      model.value.frameworkAlignments.map(alignment => loadFrameworkById(alignment.frameworkId)),
    );
  }
});

const loadFrameworks = async () => {
  // Assuming your API supports a limit or 'all' to get the list
  const response = await useHttp().get('/util/frameworks');

  if (response.statusCode === HttpStatusCode.Ok) {
    allFrameworks.value = response.data.items;
  }
};

const availableFrameworkOptions = computed(() => {
  const query = searchParams.search ? searchParams.search.toLowerCase() : '';

  const currentAlignments = model.value.frameworkAlignments || [];

  // Exclude already selected frameworks
  const unselected = allFrameworks.value.filter(framework => {
    return !currentAlignments.some(alignment => alignment.frameworkId === framework.id);
  });

  // Match Search Query
  if (!query) return unselected;

  return unselected.filter(framework => framework.name.toLowerCase().includes(query));
});

const getFrameworkName = (id) => {
  // Search for the nam ein the filtered list
  if (frameworks.value[id]?.name) return frameworks.value[id].name;

  // Fallback: If not found, search in the entire list of Frameworks
  const found = allFrameworks.value.find(f => f.id === id);
  return found ? found.name : 'Loading...';
};

const handleFrameworkSelect = async (frameworkId) => {
  // Initialize array if null
  if (!model.value.frameworkAlignments) {
    model.value.frameworkAlignments = [];
  }

  // Prevent adding the same framework twice
  const exists = model.value.frameworkAlignments.find(item => item.frameworkId === frameworkId);

  if (!exists) {
    // Add to model
    model.value.frameworkAlignments.push({
      frameworkId: frameworkId,
      goals: [], // Starts empty
    });

    // Automatically open the new accordion
    expandedFrameworks.value[frameworkId] = true;
    await loadFrameworkById(frameworkId);
  }

  // Reset search
  searchParams.search = '';
  showSearchResults.value = false;
};

// Removes a framework card and all its selected goals
const removeFramework = (index) => {
  const frameworkId = model.value.frameworkAlignments[index].frameworkId;
  delete expandedFrameworks.value[frameworkId];

  model.value.frameworkAlignments.splice(index, 1);
};

// Fetches framework
const loadFrameworkById = async (frameworkId) => {
  if (frameworks.value[frameworkId]) return;

  loadingFrameworks.value[frameworkId] = true;

  const response = await useHttp().get(`/util/frameworks/${frameworkId}`);

  if (response.statusCode === 200) {
    frameworks.value[frameworkId] = response.data;
  }

  loadingFrameworks.value[frameworkId] = false;
};

// Counts total selected goals/subgoals
const getSelectedCount = (alignment) => {
  let count = 0;

  alignment.goals.forEach(goal => {
    if (goal.subgoalIds && goal.subgoalIds.length > 0) {
      count += goal.subgoalIds.length;
    } else {
      count += 1; // Standalone goal
    }
  });

  return count;
};

// Checks if a standalone goal is in the model
const isGoalSelected = (alignment, goalId) => {
  return alignment.goals.some(goal => goal.id === goalId);
};

// Checks if a specific subgoal ID exists within the parent goal's subgoalIds array
const isSubgoalSelected = (alignment, parentId, subgoalId) => {
  const goal = alignment.goals.find(g => g.id === parentId);

  return goal && goal.subgoalIds.includes(subgoalId);
};

// Toggles a top-level goal that has NO subgoals
const toggleGoal = (alignment, goalId) => {
  const index = alignment.goals.findIndex(goal => goal.id === goalId);

  if (index !== -1) {
    alignment.goals.splice(index, 1);
  } else {
    // Add new goal object. subgoalIds initialized for consistency/schema compliance
    alignment.goals.push({ id: goalId, subgoalIds: [] });
  }
};

// Toggles a child subgoal. This implicitly manages the parent goal in the array.
const toggleSubgoal = (alignment, parentId, subgoalId) => {
  // Find or Create Parent Goal
  let goal = alignment.goals.find(goal => goal.id === parentId);

  if (!goal) {
    goal = { id: parentId, subgoalIds: [] };
    alignment.goals.push(goal);
  }

  if (!goal.subgoalIds) goal.subgoalIds = [];

  const subgoalIndex = goal.subgoalIds.indexOf(subgoalId);

  if (subgoalIndex !== -1) {
    goal.subgoalIds.splice(subgoalIndex, 1);

    // If the parent now has NO subgoals selected, remove the parent object entirely.
    // This keeps the "selected count" accurate and the data clean.
    if (goal.subgoalIds.length === 0) {
      const parentIndex = alignment.goals.indexOf(goal);

      if (parentIndex !== -1) {
        alignment.goals.splice(parentIndex, 1);
      }
    }
  } else {
    goal.subgoalIds.push(subgoalId);
  }
};

</script>
